import React, { useState, useEffect } from 'react'
import {
  Typography,
  Stack,
  Box,
  List,
  InputAdornment,
  IconButton
} from '@mui/material'
import { Button } from '@repo/ui'
import { UseFormReturn } from 'react-hook-form'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { Divider } from 'components/UI/Divider'
import { PasswordStrengthMeter } from 'components/UI/PasswordStrengthMeter'
import { FieldText } from 'components/Fields/Text'
import { FORM_VALIDATION_RULES } from 'pages/Profile/validation'
import { createApiCall } from 'services'
import { DatePickerField } from 'components/Fields/DatePickerField'
import { Input } from 'components/UI/Input'
import { PickerListItem } from 'components/UI/PickerListItem'
import { PICKER_LIST_PADDING } from 'components/UI/pickerListStyle'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { genderOptions } from 'settings'
import { useTheme } from '@mui/material/styles'

interface FormValues {
  first_name: string
  last_name: string
  username: string | null
  birthday: Date | null
  phone: string | null
  country: string | null
  gender: string | null
  email: string | null
  current_password?: string
  new_password?: string
}

interface EditFieldModalProps {
  editField: { key: keyof FormValues | 'password' | 'name'; title: string } | null
  methods: UseFormReturn<FormValues>
  originalValues: Partial<FormValues>
  countriesState: any[]
  countrySearch: string
  setCountrySearch: (value: string) => void
  passwordCurrent: string
  setPasswordCurrent: (value: string) => void
  passwordNew: string
  setPasswordNew: (value: string) => void
  showCurrentPassword: boolean
  setShowCurrentPassword: (value: boolean) => void
  showNewPassword: boolean
  setShowNewPassword: (value: boolean) => void
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  onCancel: () => void
  onRemoveField?: (key: 'username' | 'birthday' | 'gender' | 'country' | 'phone') => void
}

// 'phone' deliberately excluded: Profile intercepts field.key === 'phone'
// before it ever reaches this modal (opens PhoneVerificationDrawer instead —
// see pages/Profile/index.tsx), so editField.key can never actually be
// 'phone' here. There is intentionally no remove-phone-number affordance
// (user directive, VERA-291 follow-up audit: "leave it out") — do not add
// one back in as a "fix" for this comment.
const OPTIONAL_KEYS = ['username', 'birthday', 'gender', 'country']

// ISO 3166-1 alpha-2 code -> emoji flag (regional-indicator codepoints; renders
// from the OS font, no network/broken images).
const codeToFlagEmoji = (code?: string) =>
  code && code.length === 2
    ? String.fromCodePoint(...[...code.toUpperCase()].map(ch => 0x1f1e6 + ch.charCodeAt(0) - 65))
    : ''

const getStyles = (theme: any) => ({
  modal: {
    maxWidth: 460,
    width: '100%',
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column'
  },
  content: {
    '@media (max-width:360px)': { px: 0 }
  },
  footer: {
    // No horizontal padding so the full-width Save matches the field width
    // above it (the field spans the content box edge-to-edge). The divider
    // above the Save button (with its own top margin) is rendered as a
    // shared <Divider /> below, not a border on this Box — see its render
    // site for why the password case skips it.
    px: 0,
    backgroundColor: theme.palette.background.paper
  }
})

export const EditFieldModal: React.FC<EditFieldModalProps> = ({
  editField,
  methods,
  originalValues,
  countriesState,
  countrySearch,
  setCountrySearch,
  passwordCurrent,
  setPasswordCurrent,
  passwordNew,
  setPasswordNew,
  showCurrentPassword,
  setShowCurrentPassword,
  showNewPassword,
  setShowNewPassword,
  onClose,
  onSubmit,
  onCancel,
  onRemoveField
}) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const colors = {
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    bg: theme.palette.grey[50],
    border: theme.palette.grey[300],
    hover: theme.palette.primary.main,
    error: theme.palette.error.main,
    white: theme.palette.common.white,
  }

  const { getValues, setValue, watch } = methods
  const genderValue = (watch('gender') as string) ?? ''
  const countryValue = (watch('country') as string | null) ?? null
  const usernameValue = watch('username')
  // A "Clear" affordance is offered only for optional fields that currently hold
  // a value. Rendered in the drawer's HEADER row (next to Cancel) rather than
  // above the field content — every field here is a single field per drawer
  // (the drawer title already IS its label), so the header row is the one
  // place Clear can sit that never shifts the content beneath it (VERA-291).
  const canClear =
    !!editField && OPTIONAL_KEYS.includes(editField.key) && !!getValues(editField.key as any) && !!onRemoveField

  // Live username-availability check inside the username drawer (VRG-135).
  // Backend still enforces uniqueness on Save; this is the pre-Save signal.
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  useEffect(() => {
    if (editField?.key !== 'username') {
      setUsernameStatus('idle')
      return
    }
    const current = (usernameValue ?? '').trim()
    const original = ((originalValues.username as string) ?? '').trim()
    if (!current || current === original) {
      setUsernameStatus('idle')
      return
    }
    setUsernameStatus('checking')
    const t = setTimeout(async () => {
      try {
        const { data } = await createApiCall({
          method: 'GET',
          url: `/auth/customer/username-available?username=${encodeURIComponent(current)}`,
        })
        setUsernameStatus(data?.available ? 'available' : 'taken')
      } catch {
        setUsernameStatus('idle')
      }
    }, 400)
    return () => clearTimeout(t)
  }, [editField?.key, usernameValue, originalValues.username])

  const renderFieldContent = () => {
    if (!editField) return null

    switch (editField.key) {
      case 'password':
        return (
          <Stack gap={3}>
            <FieldText
              {...methods}
              name="current_password"
              fieldName="current_password"
              label="Current password"
              stacked
              type={showCurrentPassword ? 'text' : 'password'}
              rules={{
                required: { value: true, message: 'Current password is required' },
                minLength: { value: 8, message: 'Min length is 8 characters' },
              }}
              defaultValue={passwordCurrent}
              placeholder="Enter"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    size="small"
                    sx={{ color: colors.muted, backgroundColor: 'transparent', '&:hover': { backgroundColor: 'transparent' } }}
                  >
                    {showCurrentPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              }
              onChange={e => setPasswordCurrent(e.target.value)}
            />
            <FieldText
              {...methods}
              name="new_password"
              fieldName="new_password"
              label="New password"
              stacked
              type={showNewPassword ? 'text' : 'password'}
              rules={{
                required: { value: true, message: 'New password is required' },
                minLength: { value: 8, message: 'Min length is 8 characters' },
                pattern: {
                  value: /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()|_+\-={}\[\]\\\/><>,.:"';~`])[a-zA-Z0-9!@#$%^&*()|_+\-={}\[\]\\\/><>,.:"';~`]{8,}$/,
                  message: 'Use at least 8 chars, 1 upper, 1 lower, 1 number, 1 symbol',
                },
                validate: value => value !== getValues('current_password') || 'New password must differ from current password',
              }}
              defaultValue={passwordNew}
              placeholder="Enter"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    size="small"
                    sx={{ color: colors.muted, backgroundColor: 'transparent', '&:hover': { backgroundColor: 'transparent' } }}
                  >
                    {showNewPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              }
              onChange={e => setPasswordNew(e.target.value)}
            />
            {/*
              VERA-837: password policy is zxcvbn-style strength scoring
              (not a rules checklist) — show live client-side feedback so
              the user isn't guessing, mirroring WorkOS's server-side check.
            */}
            {/*
              Only once there is something to score. With an empty field the
              meter renders its unfilled track — a full-width grey bar that
              reads as a thick divider between the fields and the footer, which
              is exactly how it was reported.
            */}
            {passwordNew.length > 0 && <PasswordStrengthMeter password={passwordNew} sx={{ mt: 0 }} />}
            <Stack sx={{ alignItems: 'center', gap: 0.5, mt: 2, mb: 2 }}>
              <Typography variant="body3" sx={{ textAlign: 'center', color: colors.muted }}>
                Can't remember your current password?
              </Typography>
              <Typography variant="body3" sx={{ textAlign: 'center', textDecoration: 'underline', cursor: 'pointer', color: colors.text }}>
                Reset password
              </Typography>
            </Stack>
          </Stack>
        )

      case 'birthday':
        return (
          <DatePickerField
            value={getValues('birthday')}
            onChange={v => setValue('birthday', v)}
          />
        )

      case 'gender':
        return (
          <List sx={{ px: 0, py: PICKER_LIST_PADDING }}>
            {genderOptions.map((opt: any) => (
              <PickerListItem
                key={opt.value}
                label={opt.title}
                selected={genderValue === opt.value}
                onClick={() => {
                  setValue('gender', opt.value)
                  methods.trigger('gender')
                }}
              />
            ))}
          </List>
        )

      case 'country':
        return (
          <Stack gap={2}>
            <Input
              placeholder="Search"
              value={countrySearch}
              onChange={setCountrySearch}
              fullWidth
              /* Colour and the 8px gap come from `Input` itself now (see its `& > svg`
                 rules) — this was one of two call sites hand-writing them. */
              startAdornment={<SearchOutlinedIcon sx={{ fontSize: 16 }} />}
            />
            <List sx={{ maxHeight: '50vh', overflow: 'auto', px: 0, py: PICKER_LIST_PADDING }}>
              {(countriesState ?? [])
                .filter((c: any) => c.name.toLowerCase().includes(countrySearch.toLowerCase()))
                .map((c: any) => (
                  <PickerListItem
                    key={c.code}
                    label={c.name}
                    leading={
                      <Typography component="span" sx={{ fontSize: '20px', lineHeight: 1, flexShrink: 0 }}>
                        {codeToFlagEmoji(c.code)}
                      </Typography>
                    }
                    selected={countryValue === c.code}
                    onClick={() => {
                      setValue('country', c.code)
                      methods.trigger('country')
                    }}
                  />
                ))}
            </List>
          </Stack>
        )

      case 'name':
        return (
          <Stack gap={3}>
            <FieldText
              {...methods}
              name="first_name"
              fieldName="first_name"
              label="First name"
              stacked
              rules={{
                required: { value: true, message: 'This field is required' },
                maxLength: { value: 64, message: 'Max length is 64 characters' },
                minLength: { value: 1, message: 'Min length is 1 character' },
              }}
            />
            <FieldText
              {...methods}
              name="last_name"
              fieldName="last_name"
              label="Last name"
              stacked
              rules={{
                required: { value: true, message: 'This field is required' },
                maxLength: { value: 64, message: 'Max length is 64 characters' },
                minLength: { value: 1, message: 'Min length is 1 character' },
              }}
            />
          </Stack>
        )

      case 'username':
        return (
          <Stack gap={1}>
            <FieldText
              {...methods}
              name="username"
              fieldName="username"
              label="Username"
              stacked
              rules={FORM_VALIDATION_RULES.username as any}
            />
            {!methods.getFieldState('username').error && usernameStatus === 'checking' && (
              <Typography variant="body3" sx={{ color: colors.muted }}>
                Checking availability…
              </Typography>
            )}
            {!methods.getFieldState('username').error && usernameStatus === 'taken' && (
              <Typography variant="body3" sx={{ color: colors.error }}>
                That username is already taken.
              </Typography>
            )}
            {!methods.getFieldState('username').error && usernameStatus === 'available' && (
              <Typography variant="body3" sx={{ color: (theme.palette as any).success?.main || '#2e7d32' }}>
                Username is available.
              </Typography>
            )}
          </Stack>
        )

      default:
        return (
          <FieldText
            {...methods}
            name={editField.key as any}
            fieldName={editField.key as any}
            label={editField.title.replace(/^Edit\s+/i, '')}
            stacked
            placeholder={getValues('first_name') || getValues('username') || ''}
          />
        )
    }
  }

  return (
    <DrawerPopup
      title={editField?.title || ''}
      titleUppercase={false}
      fullScreen={false}
      backgroundColor={colors.white}
      titleSx={{ color: colors.text, fontSize: '20px', fontWeight: 'normal' }}
      headerRight={
        <Stack direction='row' alignItems='center' spacing={2}>
          {canClear && (
            <Typography
              onClick={() => onRemoveField!(editField!.key as any)}
              sx={{ cursor: 'pointer', color: colors.muted, fontSize: '16px', '&:hover': { color: colors.error } }}
            >
              Clear
            </Typography>
          )}
          <Typography sx={{ cursor: 'pointer', color: colors.text, fontSize: '16px' }} onClick={onCancel}>
            Cancel
          </Typography>
        </Stack>
      }
      child={
        <Stack sx={styles.modal}>
          <Stack gap={2} sx={styles.content}>
            {renderFieldContent()}
          </Stack>
          <Box sx={{ ...styles.footer, backgroundColor: colors.white }}>
            {/*
              The password case's own content already ends on the "Can't
              remember your current password? / Reset password" block, which
              reads as this drawer's own footer — a divider directly above
              Save on top of that isn't separating anything meaningful (user
              feedback, VERA-291 follow-up). Every other field keeps the
              divider, now with real top margin instead of sitting flush
              against the field above it.
            */}
            {/*
              The shared Divider sits directly above Save on every field,
              password included. It was previously excluded here because the
              "Can't remember your current password?" footer already looked
              separated — but that separation was the strength meter's empty
              track, not a real rule. With that gone, password needs the same
              divider as everything else.
            */}
            <Divider />
            <Button
              variant="primary"
              onClick={onSubmit}
              fullWidth
              disabled={
                (editField?.key === 'password' && (
                  passwordCurrent.length === 0 ||
                  passwordNew.length === 0 ||
                  methods.getFieldState('current_password').invalid ||
                  methods.getFieldState('new_password').invalid
                )) ||
                (editField?.key === 'birthday' && !getValues('birthday')) ||
                (editField?.key === 'username' && usernameStatus === 'taken')
              }
            >
              Save
            </Button>
          </Box>
        </Stack>
      }
      handleOpen={!!editField}
      handleClose={onClose}
    />
  )
}
